import React, { useState, useEffect, useCallback } from 'react';
import { dataService } from '../services/dataService';
import { LAB_TO_ARTICLE } from '../constants/labLinks';
import { confirmDialog } from '../utils/dialog';

// Lab 6 posts to a real webhook so the lesson is genuine. httpbin is a fine
// default for a person trying it out, but it makes CI depend on a free public
// service — so the E2E build overrides it with the local `echo` function.
const LAB_WEBHOOK_URL =
  process.env.REACT_APP_LAB_WEBHOOK_URL || 'https://httpbin.org/post';

export const LABS = [
  {
    id: 1,
    order: 1,
    title: 'Hello Collection',
    difficulty: 'Beginner',
    time: '3 min',
    summary: 'Create a collection with a trigger that sends you an inbox notification when a new record is added.',
    learns: ['Adding a collection node', 'Defining fields (text, select)', 'Trigger nodes (record_added)', 'Send notification action'],
    steps: [
      'Click **Deploy** below to place the pipeline on your canvas.',
      'You\'ll see 3 nodes: **Contacts** (collection) → **New Contact** (trigger) → **Notify: Contact Added** (action).',
      'Open the **Contacts** collection — it has 3 fields: **Name**, **Email**, and **Status**, plus 2 sample records.',
      'Click **+ Add record** — a draft row appears. Fill in the Name field (e.g. "Carol White"), then click the green **✓** tick to save.',
      'On **Save**, the record is written and the trigger fires — sending a notification. (Records save explicitly so a half-typed row never fires the pipeline.) Check your **Inbox** to see the confirmation with the contact details.',
    ],
    expect: 'When you add a new record to Contacts, a notification appears in your Inbox confirming the pipeline ran. Components used: Collection, Trigger, Action (send_notification).',
    nodes: [
      {
        id: 'lab1-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Contacts', description: 'A simple contacts list',
          fields: [
            { id: 'f1', name: 'Name', type: 'text' },
            { id: 'f2', name: 'Email', type: 'text' },
            { id: 'f3', name: 'Status', type: 'select', options: ['Active', 'Inactive', 'Lead'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Alice Johnson', f2: 'alice@example.com', f3: 'Active' } },
            { id: 'r2', values: { f1: 'Bob Smith', f2: 'bob@example.com', f3: 'Lead' } },
          ],
        },
      },
      {
        id: 'lab1-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'New Contact', description: 'Fires when a contact is added',
          triggerType: 'record_added', triggerCollectionId: 'lab1-col',
        },
      },
      {
        id: 'lab1-notify', type: 'actionNode', position: { x: 660, y: 200 },
        data: {
          label: 'Notify: Contact Added', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Welcome {name}!',
          notificationText: 'Pipeline executed successfully.\n\nComponents fired:\n• Collection: Contacts\n• Trigger: New Contact (record_added)\n• Action: Send Notification\n\nNew contact: {name} ({email})\nStatus: {status}\nTotal contacts: {contacts.count}',
        },
      },
    ],
    edges: [
      { id: 'lab1-e1', source: 'lab1-col', target: 'lab1-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab1-e2', source: 'lab1-trigger', target: 'lab1-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 25,
    order: 1.5,
    title: 'Data — manage your records without the canvas',
    difficulty: 'Beginner',
    time: '5 min',
    summary: 'Use the Data section to work with a collection like a spreadsheet: add and edit records in the grid, open a record\'s detail panel, search and sort, and import a CSV — while the pipeline keeps automating in the background.',
    learns: ['The Data section', 'Grid editing & record detail', 'Search and column sorting', 'CSV import & export', 'Data and pipelines share the same records'],
    steps: [
      'Click **Deploy** below — it places an **Orders** collection (with sample records) wired to a trigger + notification on your canvas.',
      'Now open **🗂️ Data** in the sidebar (Build group). The **Orders** table is already there — Data shows every collection in your workspace, canvas-built or not.',
      'Click **+ Add record**, fill in the Item field (e.g. "Desk lamp"), pick a Status, then click the green **✓** to save. Now check your **Inbox** — the pipeline fired from a record you added in Tables. Same data, same automations.',
      'Click a row\'s **number** (or the ↗ button) to open the **record detail** panel — every field editable, plus created/updated timestamps.',
      'Try the **search box** (type part of an item name) and click a **column header** to sort. Click it again to reverse, and a third time to reset.',
      'Click **📥 Import CSV** and pick any small CSV file (e.g. columns: Item, Status). Map each column to a field — or create a new field from a column — and import. Bulk imports are marked as backfill, so they don\'t fire your pipeline for every row.',
      'Click **📤 Export** to download the table as CSV — the same format Import accepts.',
    ],
    expect: 'You managed real pipeline data — adding, editing, importing and exporting records — without touching the canvas. Adding a single record from Tables triggered the pipeline (check your Inbox); the CSV bulk import deliberately did not.',
    nodes: [
      {
        id: 'lab25-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Orders', description: 'Orders to track and automate',
          fields: [
            { id: 'f1', name: 'Item', type: 'text' },
            { id: 'f2', name: 'Customer', type: 'text' },
            { id: 'f3', name: 'Status', type: 'select', options: ['Open', 'In Progress', 'Done'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Standing desk', f2: 'Acme Ltd', f3: 'Open' } },
            { id: 'r2', values: { f1: 'Monitor arm', f2: 'Globex', f3: 'In Progress' } },
          ],
        },
      },
      {
        id: 'lab25-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'New Order', description: 'Fires when an order is added',
          triggerType: 'record_added', triggerCollectionId: 'lab25-col',
        },
      },
      {
        id: 'lab25-notify', type: 'actionNode', position: { x: 660, y: 200 },
        data: {
          label: 'Notify: Order Added', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'New order: {item}',
          notificationText: 'A new order was added — from the Data grid or the canvas, same pipeline either way.\n\nItem: {item}\nCustomer: {customer}\nStatus: {status}\nTotal orders: {orders.count}',
        },
      },
    ],
    edges: [
      { id: 'lab25-e1', source: 'lab25-col', target: 'lab25-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab25-e2', source: 'lab25-trigger', target: 'lab25-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 26,
    order: 1.6,
    title: 'Boards — drag a card, fire a pipeline',
    difficulty: 'Beginner',
    time: '5 min',
    summary: 'Turn a table into a kanban board with a saved view, then drag a card to Done — and watch that one drag fire a real automation.',
    learns: ['Saved views (filters, hidden fields)', 'Kanban boards grouped by a select field', 'Moving cards updates the record', 'Field-change triggers fire from a board drag'],
    steps: [
      'Click **Deploy** below — it places a **Tasks** collection (with a Status field and sample records) wired to a **Status → Done** trigger + notification.',
      'Open **🗂️ Data** in the sidebar and select **Tasks**.',
      'Click **＋ View** in the tab row. Name it "Board", choose **📌 Kanban**, group by **Status**, and save. Your records appear as cards in Backlog / In Progress / Done columns.',
      'Drag the **"Write launch post"** card into the **Done** column (or use the little "Move to…" dropdown on the card). The card moves — and the record\'s Status field actually changes.',
      'Check your **Inbox**: the pipeline noticed the Status change and sent you a "Task completed" message. One drag = one automation run.',
      'Double-click any card to open its **record detail**. Back on the **All records** tab, note the saved **Board** view stays in the tab row — views are saved per table.',
      'Bonus: click **✎** next to the active view to add a **filter** (e.g. Status is not Done) or hide fields — saved views remember filters, layout and hidden fields.',
    ],
    expect: 'Dragging a card between kanban columns updates the underlying record, which fires the field-change trigger and drops a "Task completed" message in your Inbox. Saved views persist per table.',
    nodes: [
      {
        id: 'lab26-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Tasks', description: 'A simple task board',
          fields: [
            { id: 'f1', name: 'Title', type: 'text' },
            { id: 'f2', name: 'Owner', type: 'text' },
            { id: 'f3', name: 'Status', type: 'select', options: ['Backlog', 'In Progress', 'Done'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Write launch post', f2: 'You', f3: 'In Progress' } },
            { id: 'r2', values: { f1: 'Fix signup bug', f2: 'You', f3: 'Backlog' } },
            { id: 'r3', values: { f1: 'Ship v1', f2: 'You', f3: 'Backlog' } },
          ],
        },
      },
      {
        id: 'lab26-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'Task done', description: 'Fires when Status becomes Done',
          triggerType: 'field_change', triggerCollectionId: 'lab26-col',
          triggerFieldId: 'f3', triggerValue: 'Done',
        },
      },
      {
        id: 'lab26-notify', type: 'actionNode', position: { x: 660, y: 200 },
        data: {
          label: 'Notify: Task completed', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Task completed: {title}',
          notificationText: 'Nice — "{title}" just moved to Done.\n\nThat drag on the kanban board updated the record, which fired this pipeline. Owner: {owner}.',
        },
      },
    ],
    edges: [
      { id: 'lab26-e1', source: 'lab26-col', target: 'lab26-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab26-e2', source: 'lab26-trigger', target: 'lab26-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 27,
    order: 1.7,
    title: 'Forms — a public URL that runs your pipeline',
    difficulty: 'Beginner',
    time: '6 min',
    summary: 'Create a shareable online form for a table, submit it like a stranger on the internet would, and watch the submission become a record that fires your pipeline.',
    learns: ['Creating a public form from a table', 'Capability links (the URL is the key)', 'Form submission → record → trigger', 'Closing/reopening a form'],
    steps: [
      'Click **Deploy** below — it places an **Enquiries** collection wired to a trigger + notification (the "what happens on submit" part).',
      'Open **🗂️ Data**, select **Enquiries**, and click **🔗 Forms** in the toolbar, then **+ New form**.',
      'Give it the title "Contact us", tick which fields appear, and click **Create form**.',
      'Click **↗ Open** on the new form row — the public form opens in a new tab. This is what anyone with the link sees; no login needed.',
      'Fill it in and **Submit**. You\'ll get the thank-you screen.',
      'Back in the app: the submission is now a **record in the Enquiries table** (marked as coming from the form), and your **Inbox** has the "New enquiry" notification — the pipeline fired on the submission.',
      'Try **Close** on the form row, reload the public link — it politely refuses. **Reopen** turns it back on. The link is the key: share it to collect, close it to stop.',
    ],
    expect: 'A person with just the link (no account) submitted your form; the submission became a record, the record fired the record-added trigger, and the pipeline delivered a notification. Form → record → pipeline, end to end.',
    nodes: [
      {
        id: 'lab27-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Enquiries', description: 'Inbound enquiries from the form',
          fields: [
            { id: 'f1', name: 'Name', type: 'text' },
            { id: 'f2', name: 'Message', type: 'text' },
            { id: 'f3', name: 'Priority', type: 'select', options: ['Low', 'High'] },
          ],
        },
      },
      {
        id: 'lab27-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'New enquiry', description: 'Fires on each form submission',
          triggerType: 'record_added', triggerCollectionId: 'lab27-col',
        },
      },
      {
        id: 'lab27-notify', type: 'actionNode', position: { x: 660, y: 200 },
        data: {
          label: 'Notify: New enquiry', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'New enquiry from {name}',
          notificationText: 'Someone submitted your public form.\n\nName: {name}\nMessage: {message}\nPriority: {priority}\n\nThis pipeline ran because the form submission created a record in Enquiries.',
        },
      },
    ],
    edges: [
      { id: 'lab27-e1', source: 'lab27-col', target: 'lab27-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab27-e2', source: 'lab27-trigger', target: 'lab27-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 2,
    order: 2,
    title: 'Auto-Status Update',
    difficulty: 'Beginner',
    time: '3 min',
    summary: 'Wire a trigger and action to auto-set status on new records, then get a confirmation notification in your inbox.',
    learns: ['Trigger nodes (record_added)', 'Action nodes (update_field)', 'Chaining multiple actions', 'Inbox notifications'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'You\'ll see 4 nodes: **Tickets** → **New Ticket Added** → **Set Status to Open** → **Notify: Ticket Created**.',
      'Open the **Tickets** collection and go to **Records** — it comes with 2 sample tickets so you can see the shape of the data.',
      'Click **+ Add record**, type a Title (e.g. "Fix login bug"), leave Status blank, then click the green **✓** tick to save.',
      'On **Save**, the trigger fires — Status is set to **Open**, then a confirmation notification is sent to your **Inbox**.',
      'Check your **Inbox** to see the summary of all components that fired.',
    ],
    expect: 'Every new ticket gets its Status auto-set to "Open", then you receive an Inbox notification confirming the pipeline ran. Components used: Collection, Trigger, Action (update_field), Action (send_notification).',
    nodes: [
      {
        id: 'lab2-col', type: 'collectionNode', position: { x: 60, y: 200 },
        data: {
          label: 'Tickets', description: 'Support tickets',
          fields: [
            { id: 'f1', name: 'Title', type: 'text' },
            { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'In Progress', 'Done'] },
            { id: 'f3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Login button broken on mobile', f2: 'Open', f3: 'High' } },
            { id: 'r2', values: { f1: 'Update privacy policy link', f2: 'Done', f3: 'Low' } },
          ],
        },
      },
      {
        id: 'lab2-trigger', type: 'triggerNode', position: { x: 330, y: 200 },
        data: {
          label: 'New Ticket Added', description: 'Fires when a record is added to Tickets',
          triggerType: 'record_added', triggerCollectionId: 'lab2-col',
        },
      },
      {
        id: 'lab2-action', type: 'actionNode', position: { x: 600, y: 200 },
        data: {
          label: 'Set Status to Open', description: 'Auto-set new tickets to Open',
          actionType: 'update_field', collectionId: 'lab2-col',
          fieldId: 'f2', fieldValue: 'Open', fieldName: 'Status',
        },
      },
      {
        id: 'lab2-notify', type: 'actionNode', position: { x: 880, y: 320 },
        data: {
          label: 'Notify: Ticket Created', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Ticket Auto-Updated',
          notificationText: 'Pipeline executed successfully.\n\nComponents fired:\n• Collection: Tickets\n• Trigger: New Ticket Added (record_added)\n• Action: Set Status to Open (update_field)\n• Action: Send Notification\n\nA new ticket was added and its Status was set to Open.\nTotal tickets: {tickets.count}\nTimestamp: {timestamp}',
        },
      },
    ],
    edges: [
      { id: 'lab2-e1', source: 'lab2-col', target: 'lab2-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab2-e2', source: 'lab2-trigger', target: 'lab2-action', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab2-e3', source: 'lab2-action', target: 'lab2-col', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab2-e4', source: 'lab2-action', target: 'lab2-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 24,
    order: 2.5,
    title: 'Build it yourself — wire a pipeline from scratch',
    difficulty: 'Intermediate',
    time: '7 min',
    summary: 'The other labs hand you a pre-wired pipeline. This one deploys the nodes UNCONNECTED — you draw the edges yourself. That\'s how you learn the two things pre-wired demos hide: an action must be wired to its collection, and how multi-word field tokens work.',
    learns: ['Drawing edges between nodes', 'Connecting an action to its collection', 'Multi-word {field_name} tokens', 'Activating and testing your own build'],
    steps: [
      'Click **Deploy**. Unlike every other lab, the four nodes arrive **unconnected** — **Leads**, **New Lead**, **Set Status to Contacted**, and **Welcome the Lead**. Wiring them is your job.',
      'Drag from the **right edge of New Lead** to **Set Status to Contacted** to connect the trigger to the first action.',
      '**The important one:** the **Set Status** action doesn\'t know which collection to write to yet. Drag an edge from it **to the Leads collection** (or open the action and pick **Leads** in its Collection dropdown). Skip this and the action fails silently — you\'d only find out in **Logs**.',
      'Drag from **Set Status to Contacted** to **Welcome the Lead** to chain the notification after the update.',
      'Open **Welcome the Lead** and read its message. It uses **{full_name}** and **{email_address}** — note the field "Full Name" becomes **{full_name}**: lower-cased, spaces turned into underscores. Get this wrong and the token prints literally.',
      'Make sure the pipeline is **active** — click **▶ Run** if it isn\'t. Triggers only fire while it\'s active.',
      'Open **Leads → Records**, click **+ Add record**, fill in **Full Name** and **Email Address**, leave **Lead Status** blank, then the green **✓** tick to save.',
      'Watch: **Lead Status** flips to **Contacted** (the row flashes), and an Inbox note arrives addressed to the actual person — e.g. *"New lead: Dana Lopez (dana@example.com)"*.',
    ],
    expect: 'A pipeline YOU wired end to end: a new lead is auto-set to Contacted and a personalised welcome note lands in your Inbox. If nothing happens, check that the action is connected to Leads and the pipeline is active — exactly the failure modes the Troubleshooting guide covers. Components used: Collection, Trigger, Action (update_field), Action (send_notification).',
    nodes: [
      {
        id: 'lab24-col', type: 'collectionNode', position: { x: 80, y: 220 },
        data: {
          label: 'Leads', description: 'Inbound sales leads',
          fields: [
            { id: 'f1', name: 'Full Name', type: 'text' },
            { id: 'f2', name: 'Email Address', type: 'text' },
            { id: 'f3', name: 'Lead Status', type: 'select', options: ['New', 'Contacted', 'Qualified'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Sam Rivera', f2: 'sam@example.com', f3: 'Qualified' } },
          ],
        },
      },
      {
        id: 'lab24-trigger', type: 'triggerNode', position: { x: 80, y: 60 },
        data: {
          label: 'New Lead', description: 'Fires when a lead is added',
          triggerType: 'record_added', triggerCollectionId: 'lab24-col',
        },
      },
      {
        id: 'lab24-status', type: 'actionNode', position: { x: 420, y: 60 },
        data: {
          // Intentionally NO collectionId — the learner must wire the edge to
          // Leads (or set the dropdown) themselves. That's the lesson.
          label: 'Set Status to Contacted', description: 'Auto-set new leads to Contacted',
          actionType: 'update_field', fieldId: 'f3', fieldName: 'Lead Status', fieldValue: 'Contacted',
        },
      },
      {
        id: 'lab24-welcome', type: 'actionNode', position: { x: 760, y: 60 },
        data: {
          label: 'Welcome the Lead', description: 'Send a personalised inbox note',
          actionType: 'send_notification',
          notificationSubject: 'New lead: {full_name}',
          notificationText: 'A new lead just came in.\n\nName: {full_name}\nEmail: {email_address}\nStatus: {lead_status}\n\n(Tip: tokens are the field name, lower-cased, with spaces as underscores.)',
        },
      },
    ],
    // Deployed UNWIRED on purpose — the learner draws every edge.
    edges: [],
  },
  {
    id: 7,
    order: 3,
    title: 'Reading Pipeline Logs',
    difficulty: 'Beginner',
    time: '4 min',
    summary: 'Run a small pipeline, then use the Logs page to see exactly what happened — green for healthy runs, red for failures — and the (i) explanations for each step.',
    learns: ['Where execution history lives', 'Colour-coded run logs (green / orange / red)', 'Inspecting each step of a run', 'Using the (i) explanations to understand failures'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'You\'ll see: **Shipments** → **New Shipment** → **Mark Shipped** → **Notify: Shipped**.',
      'Open the **Shipments** collection, click **+ Add record** — Tracking "TRK-001", leave Status blank — then click the green **✓** tick to save.',
      'On **Save**, the pipeline runs. Now open the **Logs** page from the sidebar.',
      'You\'ll see a **green** run marked *completed*, with each step listed and ticked — trigger, update field, and notification.',
      'Click the **(i)** next to any step to read a plain-language explanation of what it did.',
      'Now let\'s create a failure on purpose: back on the canvas, open the **Mark Shipped** action and change its value from **Shipped** to **Delivered** (which is *not* a valid Status option). Save.',
      'Add another Shipment record. Open **Logs** again — this run is **red** (*failed*).',
      'Click the **(i)** on the failed step — it explains that "Delivered" isn\'t a valid option for the Status field, and how to fix it.',
      'These logs are saved to your workspace, so you can log out and still review them later — handy for spotting an overnight schedule that quietly stopped.',
    ],
    expect: 'You can find any pipeline run in the Logs page, read its colour-coded status, inspect each step, and use the (i) explanations to understand why a run succeeded or failed. Components used: Collection, Trigger, Action (update_field), Action (send_notification).',
    nodes: [
      {
        id: 'lab7-col', type: 'collectionNode', position: { x: 80, y: 200 },
        data: {
          label: 'Shipments', description: 'Outbound shipments',
          fields: [
            { id: 'f1', name: 'Tracking', type: 'text' },
            { id: 'f2', name: 'Status', type: 'select', options: ['Pending', 'Shipped'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'TRK-100', f2: 'Shipped' } },
            { id: 'r2', values: { f1: 'TRK-101', f2: 'Pending' } },
          ],
        },
      },
      {
        id: 'lab7-trigger', type: 'triggerNode', position: { x: 340, y: 200 },
        data: {
          label: 'New Shipment', description: 'Fires when a shipment is added',
          triggerType: 'record_added', triggerCollectionId: 'lab7-col',
        },
      },
      {
        id: 'lab7-update', type: 'actionNode', position: { x: 600, y: 200 },
        data: {
          label: 'Mark Shipped', description: 'Set Status to Shipped',
          actionType: 'update_field', collectionId: 'lab7-col',
          fieldId: 'f2', fieldValue: 'Shipped', fieldName: 'Status',
        },
      },
      {
        id: 'lab7-notify', type: 'actionNode', position: { x: 860, y: 320 },
        data: {
          label: 'Notify: Shipped', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Shipment processed',
          notificationText: 'Pipeline executed successfully.\n\nComponents fired:\n• Collection: Shipments\n• Trigger: New Shipment (record_added)\n• Action: Mark Shipped (update_field)\n• Action: Send Notification\n\nNow open the Logs page to see this run in green.\nTotal shipments: {shipments.count}\nTimestamp: {timestamp}',
        },
      },
    ],
    edges: [
      { id: 'lab7-e1', source: 'lab7-col', target: 'lab7-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab7-e2', source: 'lab7-trigger', target: 'lab7-update', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab7-e3', source: 'lab7-update', target: 'lab7-col', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab7-e4', source: 'lab7-update', target: 'lab7-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 3,
    order: 4,
    title: 'Condition Branching',
    difficulty: 'Intermediate',
    time: '5 min',
    summary: 'Add smart routing with conditions. Both paths send a confirmation notification to your inbox.',
    learns: ['Condition nodes (YES/NO branching)', 'Operators (equals, not equals)', 'Multiple actions from one trigger', 'Send notification action'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'You\'ll see: **Tasks** → **New Task** → **Is High Priority?** → two paths, each ending with a notification.',
      'The YES path sends a **High Priority Alert** then a confirmation. The NO path auto-updates status then confirms.',
      'Open the **Tasks** collection, click **+ Add record**: Title "Deploy update", Priority **High**, Status **Open**, then click the green **✓** tick to save.',
      'On **Save**, the trigger fires and routes to the condition. Since Priority is High, the YES path sends a notification.',
      'Check your **Inbox** — you\'ll see the high-priority alert AND a confirmation notification.',
      'Add another record (remember to click the green **✓** tick to save) with Priority **Low** — the NO path auto-updates Status and sends a different confirmation.',
    ],
    expect: 'Both YES and NO condition paths end with a notification in your Inbox. High-priority tasks get an alert + confirmation. Low/Medium tasks get auto-updated + confirmation.',
    nodes: [
      {
        id: 'lab3-col', type: 'collectionNode', position: { x: 50, y: 220 },
        data: {
          label: 'Tasks', description: 'Task tracker with priorities',
          fields: [
            { id: 'f1', name: 'Title', type: 'text' },
            { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'In Progress', 'Review', 'Done'] },
            { id: 'f3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Write release notes', f2: 'Open', f3: 'Medium' } },
            { id: 'r2', values: { f1: 'Fix payment timeout', f2: 'In Progress', f3: 'High' } },
          ],
        },
      },
      {
        id: 'lab3-trigger', type: 'triggerNode', position: { x: 300, y: 220 },
        data: {
          label: 'New Task', description: 'Fires when a task is added',
          triggerType: 'record_added', triggerCollectionId: 'lab3-col',
        },
      },
      {
        id: 'lab3-cond', type: 'conditionNode', position: { x: 550, y: 220 },
        data: {
          label: 'Is High Priority?', description: 'Check if priority is High',
          conditions: [{ collectionId: 'lab3-col', fieldId: 'f3', fieldName: 'Priority', operator: 'eq', value: 'High' }],
          logic: 'and',
        },
      },
      {
        id: 'lab3-action-yes', type: 'actionNode', position: { x: 830, y: 100 },
        data: {
          label: 'Notify: High Priority', description: 'Send alert for high-priority tasks',
          actionType: 'send_notification',
          notificationSubject: 'High Priority Task Alert',
          notificationText: 'A high-priority task was added to the Tasks collection.\nPriority: {priority}\nTimestamp: {timestamp}',
        },
      },
      {
        id: 'lab3-confirm-yes', type: 'actionNode', position: { x: 1120, y: 100 },
        data: {
          label: 'Confirm: YES Path', description: 'Lab confirmation — YES branch',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — YES Path (High Priority)',
          notificationText: 'Pipeline executed successfully via YES branch.\n\nComponents fired:\n• Collection: Tasks\n• Trigger: New Task (record_added)\n• Condition: Is High Priority? → YES\n• Action: Notify High Priority\n• Action: Confirmation\n\nTotal tasks: {tasks.count}\nTimestamp: {timestamp}',
        },
      },
      {
        id: 'lab3-action-no', type: 'actionNode', position: { x: 830, y: 360 },
        data: {
          label: 'Auto-progress', description: 'Move non-urgent tasks forward',
          actionType: 'update_field', collectionId: 'lab3-col',
          fieldId: 'f2', fieldValue: 'In Progress', fieldName: 'Status',
        },
      },
      {
        id: 'lab3-confirm-no', type: 'actionNode', position: { x: 1120, y: 360 },
        data: {
          label: 'Confirm: NO Path', description: 'Lab confirmation — NO branch',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — NO Path (Auto-Progress)',
          notificationText: 'Pipeline executed successfully via NO branch.\n\nComponents fired:\n• Collection: Tasks\n• Trigger: New Task (record_added)\n• Condition: Is High Priority? → NO\n• Action: Auto-progress (update_field)\n• Action: Confirmation\n\nTotal tasks: {tasks.count}\nTimestamp: {timestamp}',
        },
      },
    ],
    edges: [
      { id: 'lab3-e1', source: 'lab3-col', target: 'lab3-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab3-e2', source: 'lab3-trigger', target: 'lab3-cond', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab3-e3', source: 'lab3-cond', target: 'lab3-action-yes', sourceHandle: 'yes', animated: true, style: { stroke: '#22c55e' } },
      { id: 'lab3-e4', source: 'lab3-action-yes', target: 'lab3-confirm-yes', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab3-e5', source: 'lab3-cond', target: 'lab3-action-no', sourceHandle: 'no', animated: true, style: { stroke: '#ef4444' } },
      { id: 'lab3-e6', source: 'lab3-action-no', target: 'lab3-col', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab3-e7', source: 'lab3-action-no', target: 'lab3-confirm-no', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 8,
    order: 5,
    title: 'Send Message — Recipients & Channels',
    difficulty: 'Intermediate',
    time: '5 min',
    summary: 'Use the unified Send Message action to notify a recipient pulled from a collection field, choose channels (inbox now; email/WhatsApp for later), and personalise the message with record data.',
    learns: ['The Send Message action: WHO / HOW / WHAT', 'Recipient from a collection field (e.g. customer email)', 'Channel selection (inbox, email, WhatsApp)', 'Personalising the message with {record fields}'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'You\'ll see: **Customers** → **New Customer** → **Welcome Message**.',
      'Open the **Welcome Message** action. Notice the three sections: **WHO** (recipient is the Customer\'s Email field), **HOW** (channels — Inbox is on, Email is ticked for later), and **WHAT** (a personalised subject and body).',
      'Open the **Customers** collection, click **+ Add record** — Name "Maria", Email "maria@example.com", Plan "Pro" — then click the green **✓** tick to save.',
      'On **Save**, the **New Customer** trigger fires and the Send Message action runs.',
      'Check your **Inbox** — the message is addressed to maria@example.com and personalised: "Welcome Maria — your Pro plan is active".',
      'Email and WhatsApp need an external provider (SendGrid / Twilio), so for now those channels are recorded on the message and delivered to the Inbox while testing. Once a provider is connected, the *same* action sends externally — no rewiring needed.',
    ],
    expect: 'A new customer triggers a personalised Send Message resolved to their email address, delivered to the Inbox for testing with email queued for external send later. Components used: Collection, Trigger, Action (send_notification with recipient + channels).',
    nodes: [
      {
        id: 'lab8-col', type: 'collectionNode', position: { x: 80, y: 200 },
        data: {
          label: 'Customers', description: 'Customer signups',
          fields: [
            { id: 'f1', name: 'Name', type: 'text' },
            { id: 'f2', name: 'Email', type: 'text' },
            { id: 'f3', name: 'Plan', type: 'select', options: ['Free', 'Pro', 'Enterprise'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'James Carter', f2: 'james@example.com', f3: 'Pro' } },
            { id: 'r2', values: { f1: 'Lena Park', f2: 'lena@example.com', f3: 'Free' } },
          ],
        },
      },
      {
        id: 'lab8-trigger', type: 'triggerNode', position: { x: 340, y: 200 },
        data: {
          label: 'New Customer', description: 'Fires when a customer is added',
          triggerType: 'record_added', triggerCollectionId: 'lab8-col',
        },
      },
      {
        id: 'lab8-msg', type: 'actionNode', position: { x: 600, y: 200 },
        data: {
          label: 'Welcome Message', description: 'Send a personalised welcome',
          actionType: 'send_notification',
          recipientType: 'collection_field',
          recipientCollectionId: 'lab8-col',
          recipientFieldId: 'f2',
          messageChannels: { inbox: true, email: true, whatsapp: false },
          notificationSubject: 'Welcome {name} — your {plan} plan is active',
          notificationText: 'Hi {name},\n\nThanks for joining! Your {plan} plan is now active.\n\nThis message was sent to {email}.\n\n(While testing, every channel is delivered to your Inbox. Connect SendGrid/Twilio later to send email/WhatsApp for real.)',
        },
      },
    ],
    edges: [
      { id: 'lab8-e1', source: 'lab8-col', target: 'lab8-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab8-e2', source: 'lab8-trigger', target: 'lab8-msg', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 4,
    order: 6,
    title: 'Task Queue & Inbox',
    difficulty: 'Intermediate',
    time: '5 min',
    summary: 'Build a human task queue with a Function node. When tasks are completed, records are archived and you get a confirmation notification.',
    learns: ['Function nodes as task queues', 'Task fields & task creation', 'function_task_change trigger', 'Create record action', 'Chained notification action'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'You\'ll see 5 nodes: **Bug Tracker** → **Task Completed** → **Log to Archive** → **Notify: Task Archived** + **Archive** collection.',
      'Click the **Bug Tracker** function node and open the **Tasks** view.',
      'Click **+ New Task** and fill in: Title "Fix header alignment", Status "To do", Priority "Medium".',
      'Change the task\'s Status to **Done** using the dropdown.',
      'The trigger fires — check the **Archive** collection for a new record, then check your **Inbox** for a confirmation.',
    ],
    expect: 'Completing a task archives it and sends a confirmation notification to your Inbox. Components used: Function, Trigger (function_task_change), Action (create_record), Collection, Action (send_notification).',
    nodes: [
      {
        id: 'lab4-fn', type: 'functionNode', position: { x: 60, y: 180 },
        data: {
          label: 'Bug Tracker', description: 'Track and assign bugs',
          agents: [], tasks: [], autoAssign: 'manual',
          taskFields: [
            { id: 'tf1', name: 'Title', type: 'text', required: true },
            { id: 'tf2', name: 'Status', type: 'select', options: ['To do', 'In Progress', 'Review', 'Done'], default: 'To do' },
            { id: 'tf3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
            { id: 'tf4', name: 'Notes', type: 'text' },
          ],
        },
      },
      {
        id: 'lab4-trigger', type: 'triggerNode', position: { x: 340, y: 180 },
        data: {
          label: 'Task Completed', description: 'Fires when a bug is marked Done',
          triggerType: 'function_task_change',
          triggerFunctionId: 'lab4-fn', triggerTaskFieldId: 'tf2', triggerTaskValue: 'Done',
        },
      },
      {
        id: 'lab4-action', type: 'actionNode', position: { x: 620, y: 180 },
        data: {
          label: 'Log to Archive', description: 'Create a record in the archive',
          actionType: 'create_record', collectionId: 'lab4-archive',
          recordValues: { f1: 'Completed bug — {date}', f2: 'Resolved', f3: 'See Bug Tracker' },
        },
      },
      {
        id: 'lab4-notify', type: 'actionNode', position: { x: 900, y: 180 },
        data: {
          label: 'Notify: Task Archived', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Task Archived',
          notificationText: 'Pipeline executed successfully.\n\nComponents fired:\n• Function: Bug Tracker\n• Trigger: Task Completed (function_task_change)\n• Action: Log to Archive (create_record)\n• Collection: Archive\n• Action: Send Notification\n\nBug Tracker tasks: {bug_tracker.task_count} total, {bug_tracker.pending_count} pending.\nArchive records: {archive.count}',
        },
      },
      {
        id: 'lab4-archive', type: 'collectionNode', position: { x: 620, y: 380 },
        data: {
          label: 'Archive', description: 'Completed bug records',
          fields: [
            { id: 'f1', name: 'Bug Title', type: 'text' },
            { id: 'f2', name: 'Resolution', type: 'select', options: ['Resolved', 'Won\'t Fix', 'Duplicate'] },
            { id: 'f3', name: 'Original Priority', type: 'text' },
          ],
          records: [
            { id: 'r1', values: { f1: 'Navbar overlaps hero on tablet', f2: 'Resolved', f3: 'High' } },
          ],
        },
      },
    ],
    edges: [
      { id: 'lab4-e1', source: 'lab4-fn', target: 'lab4-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab4-e2', source: 'lab4-trigger', target: 'lab4-action', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab4-e3', source: 'lab4-action', target: 'lab4-notify', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab4-e4', source: 'lab4-action', target: 'lab4-archive', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 5,
    order: 8,
    title: 'Full Pipeline — End to End',
    difficulty: 'Advanced',
    time: '10 min',
    summary: 'Build a complete automation pipeline using all core components. Both approval paths end with a confirmation notification to your inbox.',
    learns: [
      'Combining all node types in one pipeline',
      'Gate approval workflow',
      'Connection nodes for external services',
      'Notes for documentation',
      'Multi-path automation flows',
      'Confirmation notifications on every path',
    ],
    steps: [
      'Click **Deploy** to place the full pipeline on your canvas.',
      'The pipeline models an **Order Processing** workflow with 11 connected nodes.',
      '**Start here:** Open the **Orders** collection, click **+ Add record** — Title: "Widget order", Amount: "500", Status: "New" — then click the green **✓** tick to save.',
      'On **Save**, the **New Order** trigger fires and routes to the **High Value?** condition.',
      'If Amount contains "500" or more, the YES path sends it to the **Manager Approval** gate.',
      'Open the gate and **approve** the order. This triggers **Process Order** → **Fulfilment** → **Confirm: Approved**.',
      'For low-value orders (Amount < 500), the NO path auto-processes and sends **Confirm: Auto-Processed**.',
      'Check your **Inbox** — you\'ll see a notification confirming which path executed and all components involved.',
      'Check the **API Endpoint** connection node and read the **Note** node for workflow documentation.',
    ],
    expect: 'A fully connected pipeline with confirmation notifications on both paths. Every execution ends with an Inbox notification listing all components that fired. Components used: Collection, Trigger, Condition, Gate, Actions, Function, Connection, Note.',
    nodes: [
      {
        id: 'lab5-orders', type: 'collectionNode', position: { x: 50, y: 250 },
        data: {
          label: 'Orders', description: 'Incoming orders',
          fields: [
            { id: 'f1', name: 'Title', type: 'text' },
            { id: 'f2', name: 'Amount', type: 'text' },
            { id: 'f3', name: 'Status', type: 'select', options: ['New', 'Processing', 'Approved', 'Shipped', 'Cancelled'] },
            { id: 'f4', name: 'Customer', type: 'text' },
          ],
          records: [
            { id: 'r1', values: { f1: 'Starter widget order', f2: '120', f3: 'Processing', f4: 'Acme Co' } },
            { id: 'r2', values: { f1: 'Bulk enterprise order', f2: '4800', f3: 'Approved', f4: 'Globex Inc' } },
          ],
        },
      },
      {
        id: 'lab5-trigger', type: 'triggerNode', position: { x: 300, y: 250 },
        data: {
          label: 'New Order', description: 'Fires when a new order is added',
          triggerType: 'record_added', triggerCollectionId: 'lab5-orders',
        },
      },
      {
        id: 'lab5-cond', type: 'conditionNode', position: { x: 540, y: 250 },
        data: {
          label: 'High Value?', description: 'Check if amount >= 500',
          conditions: [{ collectionId: 'lab5-orders', fieldId: 'f2', fieldName: 'Amount', operator: 'gte', value: '500' }],
          logic: 'and',
        },
      },
      {
        id: 'lab5-gate', type: 'gateNode', position: { x: 800, y: 120 },
        data: {
          label: 'Manager Approval', description: 'High-value orders need sign-off',
          approver: 'Manager', criteria: 'Verify order details and customer history before approving',
          autoApprove: 'never',
        },
      },
      {
        id: 'lab5-action-approve', type: 'actionNode', position: { x: 1060, y: 120 },
        data: {
          label: 'Process Order', description: 'Update status after approval',
          actionType: 'update_field', collectionId: 'lab5-orders',
          fieldId: 'f3', fieldValue: 'Processing', fieldName: 'Status',
        },
      },
      {
        id: 'lab5-fn', type: 'functionNode', position: { x: 1320, y: 120 },
        data: {
          label: 'Fulfilment', description: 'Packing and shipping team',
          agents: [], tasks: [], autoAssign: 'manual',
          taskFields: [
            { id: 'tf1', name: 'Order', type: 'text', required: true },
            { id: 'tf2', name: 'Status', type: 'select', options: ['To do', 'Packing', 'Shipped', 'Done'], default: 'To do' },
            { id: 'tf3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High'] },
          ],
        },
      },
      {
        id: 'lab5-confirm-yes', type: 'actionNode', position: { x: 1580, y: 120 },
        data: {
          label: 'Confirm: Approved', description: 'Notification for approved path',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Order Approved',
          notificationText: 'Pipeline executed successfully via APPROVAL path.\n\nComponents fired:\n• Collection: Orders\n• Trigger: New Order (record_added)\n• Condition: High Value? → YES\n• Gate: Manager Approval\n• Action: Process Order (update_field)\n• Function: Fulfilment\n• Action: Confirmation\n\nTotal orders: {orders.count}\nTimestamp: {timestamp}',
        },
      },
      {
        id: 'lab5-action-auto', type: 'actionNode', position: { x: 800, y: 400 },
        data: {
          label: 'Auto-Process', description: 'Low-value orders skip approval',
          actionType: 'update_field', collectionId: 'lab5-orders',
          fieldId: 'f3', fieldValue: 'Processing', fieldName: 'Status',
        },
      },
      {
        id: 'lab5-confirm-no', type: 'actionNode', position: { x: 1060, y: 400 },
        data: {
          label: 'Confirm: Auto-Processed', description: 'Notification for auto path',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Order Auto-Processed',
          notificationText: 'Pipeline executed successfully via AUTO-PROCESS path.\n\nComponents fired:\n• Collection: Orders\n• Trigger: New Order (record_added)\n• Condition: High Value? → NO\n• Action: Auto-Process (update_field)\n• Action: Confirmation\n\nTotal orders: {orders.count}\nTimestamp: {timestamp}',
        },
      },
      {
        id: 'lab5-conn', type: 'connectionNode', position: { x: 1580, y: 400 },
        data: {
          label: 'API Endpoint', description: 'External shipping API',
          serviceType: 'webhook', serviceUrl: LAB_WEBHOOK_URL,
          authType: 'none',
        },
      },
      {
        id: 'lab5-note', type: 'noteNode', position: { x: 50, y: 50 },
        data: {
          title: 'Order Pipeline Rules',
          body: 'Orders >= 500 require manager approval.\nOrders < 500 are auto-processed.\nBoth paths send an Inbox notification on completion.\nAPI Endpoint is for future webhook integration.',
          color: '#fef9c3',
        },
      },
    ],
    edges: [
      { id: 'lab5-e1', source: 'lab5-orders', target: 'lab5-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e2', source: 'lab5-trigger', target: 'lab5-cond', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e3', source: 'lab5-cond', target: 'lab5-gate', sourceHandle: 'yes', animated: true, style: { stroke: '#22c55e' } },
      { id: 'lab5-e4', source: 'lab5-gate', target: 'lab5-action-approve', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e5a', source: 'lab5-action-approve', target: 'lab5-orders', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e5', source: 'lab5-action-approve', target: 'lab5-fn', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e6', source: 'lab5-fn', target: 'lab5-confirm-yes', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e7', source: 'lab5-cond', target: 'lab5-action-auto', sourceHandle: 'no', animated: true, style: { stroke: '#ef4444' } },
      { id: 'lab5-e8a', source: 'lab5-action-auto', target: 'lab5-orders', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e8', source: 'lab5-action-auto', target: 'lab5-confirm-no', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e9', source: 'lab5-confirm-yes', target: 'lab5-conn', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab5-e10', source: 'lab5-confirm-no', target: 'lab5-conn', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 6,
    order: 7,
    title: 'Social Media Post — Connectors',
    difficulty: 'Intermediate',
    time: '5 min',
    summary: 'Post to an external service through a Connection. Add a post and the pipeline sends it out over HTTP, marks it Posted, and notifies you.',
    learns: [
      'Connection nodes (external services)',
      'HTTP request action',
      'Wiring an action to a connection (the connector is the target)',
      'Injecting record data into a request body with {tokens}',
    ],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas.',
      'Open the **Outbound Post** connection node. By default it points to **https://httpbin.org/post** — a free echo endpoint so you can test immediately with no signup.',
      'To post for real, replace the URL with a **Discord** or **Slack** incoming webhook URL (both accept a simple POST, no OAuth needed).',
      'Open the **Posts** collection, click **+ Add record** — set **Message** to anything, e.g. "Hello from my pipeline!" — then click the green **✓** tick to save.',
      'On **Save**, the **New Post** trigger fires → the **Send Post** action POSTs the message to the connection → **Status** is set to **Posted** → you get an Inbox confirmation.',
      'With httpbin, the request succeeds and is echoed back. With a Discord/Slack webhook, your message actually appears in the channel.',
      'Try deleting the connector between **Send Post** and **Outbound Post** — the action will fail (no target), proving the wiring is what drives it.',
      'Note: a Connection node is for endpoints like this one, which have no data behind them. If you are working with a collection fed by a connector (see **Connect another app**), you do **not** need one — the collection names its connector and actions follow it to the saved credential.',
    ],
    expect: 'Adding a post sends it over HTTP through the connected service, sets Status to Posted, and drops a confirmation in your Inbox. Components used: Collection, Trigger, Action (http_request), Connection, Action (update_field), Action (send_notification).',
    nodes: [
      {
        id: 'lab6-col', type: 'collectionNode', position: { x: 60, y: 200 },
        data: {
          label: 'Posts', description: 'Social posts to publish',
          fields: [
            { id: 'f1', name: 'Message', type: 'text' },
            { id: 'f2', name: 'Status', type: 'select', options: ['Draft', 'Posted'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Launch day is here! 🚀', f2: 'Posted' } },
            { id: 'r2', values: { f1: 'Behind the scenes of our build', f2: 'Draft' } },
          ],
        },
      },
      {
        id: 'lab6-trigger', type: 'triggerNode', position: { x: 320, y: 200 },
        data: {
          label: 'New Post', description: 'Fires when a post is added',
          triggerType: 'record_added', triggerCollectionId: 'lab6-col',
        },
      },
      {
        id: 'lab6-action', type: 'actionNode', position: { x: 580, y: 200 },
        data: {
          label: 'Send Post', description: 'POST the message to the connected service',
          actionType: 'http_request',
          connectionNodeId: 'lab6-conn',
          httpEndpoint: '', httpMethod: 'POST',
          httpBody: '{"content":"{record.message}","posted_at":"{timestamp}"}',
        },
      },
      {
        id: 'lab6-conn', type: 'connectionNode', position: { x: 580, y: 380 },
        data: {
          label: 'Outbound Post', description: 'Webhook target (httpbin by default)',
          serviceType: 'webhook', serviceUrl: LAB_WEBHOOK_URL,
          authType: 'none',
        },
      },
      {
        id: 'lab6-update', type: 'actionNode', position: { x: 860, y: 200 },
        data: {
          label: 'Mark as Posted', description: 'Set the post Status to Posted',
          actionType: 'update_field',
          fieldId: 'f2', fieldValue: 'Posted', fieldName: 'Status',
        },
      },
      {
        id: 'lab6-notify', type: 'actionNode', position: { x: 1120, y: 320 },
        data: {
          label: 'Notify: Post Sent', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Post Sent',
          notificationText: 'Pipeline executed successfully.\n\nComponents fired:\n• Collection: Posts\n• Trigger: New Post (record_added)\n• Action: Send Post (http_request)\n• Connection: Outbound Post\n• Action: Mark as Posted (update_field)\n• Action: Send Notification\n\nMessage: {record.message}\nTotal posts: {posts.count}\nTimestamp: {timestamp}',
        },
      },
    ],
    edges: [
      { id: 'lab6-e1', source: 'lab6-col', target: 'lab6-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab6-e2', source: 'lab6-trigger', target: 'lab6-action', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab6-e3', source: 'lab6-action', target: 'lab6-conn', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab6-e4', source: 'lab6-action', target: 'lab6-update', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab6-e5', source: 'lab6-update', target: 'lab6-col', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab6-e6', source: 'lab6-update', target: 'lab6-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 20,
    order: 9,
    title: 'Connect via the REST API',
    difficulty: 'Intermediate',
    time: '6 min',
    summary: 'Generate an API key and create a record from outside Projectly with a single curl command — then watch the pipeline automation fire server-side.',
    learns: ['Generating a workspace API key', 'Authenticating with a Bearer key', 'POSTing a record to a collection', 'How API writes trigger automations'],
    steps: [
      'Click **Deploy** to place the pipeline on your canvas: **Signups** → **New Signup** → **Notify: Signup**.',
      'Open your user menu (bottom-left) → **Workspace settings** → **API Keys**.',
      'Name a key (e.g. "Lab key"), choose **30 days**, and click **Generate key**. Copy it now — it is shown only once.',
      'Find the **Signups** collection id: open the collection on the canvas; the id appears in the URL/title. (Or list collections via the API in the next step.)',
      'List your collections to confirm the key works:\n`curl -H "Authorization: Bearer pk_your_key" https://projectly-dev.web.app/api/v1/collections`',
      'Create a record (replace COLLECTION_ID):\n`curl -X POST -H "Authorization: Bearer pk_your_key" -H "Content-Type: application/json" -d \'{"values":{"Name":"Dana Lopez","Email":"dana@example.com"}}\' https://projectly-dev.web.app/api/v1/collections/COLLECTION_ID/records`',
      'The API write fires the **New Signup** trigger on the server. Check your **Inbox** for the confirmation — no browser needed.',
    ],
    expect: 'A record created through the REST API appears in the Signups collection and triggers the same automation as an in-app change. Components used: Collection, Trigger (record_added), Action (send_notification), plus a workspace API key.',
    nodes: [
      {
        id: 'lab20-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Signups', description: 'Inbound signups from your website',
          fields: [
            { id: 'f1', name: 'Name', type: 'text' },
            { id: 'f2', name: 'Email', type: 'text' },
          ],
          records: [
            { id: 'r1', values: { f1: 'Dana Lopez', f2: 'dana@example.com' } },
            { id: 'r2', values: { f1: 'Omar Reed', f2: 'omar@example.com' } },
          ],
        },
      },
      {
        id: 'lab20-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'New Signup', description: 'Fires when a signup record is added',
          triggerType: 'record_added', triggerCollectionId: 'lab20-col',
        },
      },
      {
        id: 'lab20-notify', type: 'actionNode', position: { x: 660, y: 200 },
        data: {
          label: 'Notify: Signup', description: 'Send inbox confirmation',
          actionType: 'send_notification',
          notificationSubject: 'Lab complete — Signup via API',
          notificationText: 'Pipeline executed successfully from an external API call.\n\nComponents fired:\n• Collection: Signups\n• Trigger: New Signup (record_added)\n• Action: Send Notification\n\nNew signup: {name} ({email})\nTotal signups: {signups.count}',
        },
      },
    ],
    edges: [
      { id: 'lab20-e1', source: 'lab20-col', target: 'lab20-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab20-e2', source: 'lab20-trigger', target: 'lab20-notify', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 21,
    order: 10,
    title: 'Invite your team & set roles',
    difficulty: 'Beginner',
    time: '5 min',
    summary: 'Add a teammate to your workspace, give them a role, and see how permissions shape what they can do.',
    learns: ['Workspace members & roles', 'Inviting by email', 'What each role can do', 'Read-only vs edit access'],
    steps: [
      'Click **Deploy** to place a small pipeline: a **Requests** collection feeding a **Manager Approval** gate.',
      'Open your user menu (bottom-left) → **Workspace settings** → **Members**.',
      'Enter a teammate\'s email, choose a role from the dropdown, and click **Add member**. (They must already have a Projectly account.)',
      'Try **Viewer** first: a viewer can open the pipeline and read records, but the node panel shows a 🔒 read-only banner and every field is disabled.',
      'Now change their role to **Approver**: they still can\'t edit the pipeline, but they can clear the **Manager Approval** gate from their **Inbox**.',
      'Use **Editor** for someone who should build automations, and **Service Desk** for someone who only updates record statuses.',
      'Owners and Admins can change roles or remove members anytime from this same screen.',
    ],
    expect: 'You can invite members, assign roles, and understand how each role gates the UI and the server. Components used: Collection, Gate, plus Workspace settings → Members.',
    nodes: [
      {
        id: 'lab21-col', type: 'collectionNode', position: { x: 100, y: 200 },
        data: {
          label: 'Requests', description: 'Incoming requests awaiting approval',
          fields: [
            { id: 'f1', name: 'Title', type: 'text' },
            { id: 'f2', name: 'Amount', type: 'number' },
            { id: 'f3', name: 'Status', type: 'select', options: ['Pending', 'Approved', 'Rejected'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'New laptop', f2: 1200, f3: 'Pending' } },
          ],
        },
      },
      {
        id: 'lab21-gate', type: 'gateNode', position: { x: 400, y: 200 },
        data: {
          label: 'Manager Approval', description: 'Requires a manager to sign off',
          approver: 'Team Manager',
          criteria: 'Approve requests that fit the budget.',
          autoApprove: 'never',
        },
      },
    ],
    edges: [
      { id: 'lab21-e1', source: 'lab21-col', target: 'lab21-gate', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 22,
    order: 11,
    title: 'Read your Knowledge Base',
    difficulty: 'Beginner',
    time: '4 min',
    summary: 'Deploy a multi-team pipeline, then see it automatically documented in the Knowledge Base — no writing required.',
    learns: ['Auto-generated documentation', 'Reading function & agent summaries', 'Sharing pipeline structure with non-editors'],
    steps: [
      'Click **Deploy** to place a small two-team pipeline: **Support** and **Engineering** functions sharing a **Tickets** collection.',
      'Open the **Knowledge Base** from the sidebar.',
      'Notice each function is listed with its agents, roles, and a summary of its playbook.',
      'Find the **Tickets** collection entry — it shows every field and type.',
      'Edit the pipeline (rename a function or add an agent) and return to the Knowledge Base — it updates automatically.',
      'Share this view with a stakeholder who needs to understand the process but shouldn\'t edit the canvas.',
    ],
    expect: 'The Knowledge Base reflects your live pipeline structure and updates as you change it. Components used: Function (×2), Collection, plus the Knowledge Base page.',
    nodes: [
      {
        id: 'lab22-support', type: 'functionNode', position: { x: 80, y: 120 },
        data: {
          label: 'Support', description: 'Front-line customer support',
          playbook: 'Triage incoming tickets, resolve common issues, and escalate anything technical to Engineering.',
          agents: [
            { id: 'a1', name: 'Sam', role: 'Support Lead', isManager: true },
            { id: 'a2', name: 'Riya', role: 'Support Agent', isManager: false },
          ],
          tasks: [],
        },
      },
      {
        id: 'lab22-eng', type: 'functionNode', position: { x: 80, y: 300 },
        data: {
          label: 'Engineering', description: 'Handles escalated technical issues',
          playbook: 'Investigate escalated tickets, fix bugs, and update the ticket with the resolution.',
          agents: [
            { id: 'a3', name: 'Dev', role: 'Engineer', isManager: true },
          ],
          tasks: [],
        },
      },
      {
        id: 'lab22-col', type: 'collectionNode', position: { x: 400, y: 200 },
        data: {
          label: 'Tickets', description: 'Shared ticket queue',
          fields: [
            { id: 'f1', name: 'Subject', type: 'text' },
            { id: 'f2', name: 'Status', type: 'select', options: ['Open', 'Escalated', 'Resolved'] },
            { id: 'f3', name: 'Priority', type: 'select', options: ['Low', 'Medium', 'High'] },
          ],
          records: [
            { id: 'r1', values: { f1: 'Cannot reset password', f2: 'Open', f3: 'High' } },
            { id: 'r2', values: { f1: 'Feature request: dark mode', f2: 'Escalated', f3: 'Medium' } },
          ],
        },
      },
    ],
    edges: [
      { id: 'lab22-e1', source: 'lab22-support', target: 'lab22-col', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab22-e2', source: 'lab22-eng', target: 'lab22-col', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 23,
    order: 12,
    title: 'Your first agent',
    difficulty: 'Intermediate',
    time: '8 min',
    summary: 'Give a Function an agent, have a pipeline step hand it a real decision, and branch on what it decides.',
    learns: [
      'Adding an agent to a Function',
      'Team playbook vs an agent\'s own instructions',
      'Having a step ask an agent, and waiting for the answer',
      'Branching on the decision the agent reached',
      'What the run cost, and where to read what happened',
    ],
    steps: [
      'This lab spends money on **your** Anthropic key. Open **Connectors → AI** and paste a key first — nothing here runs without one, and Projectly never falls back to anyone else\'s.',
      'Click **Deploy** to place the pipeline on your canvas.',
      'Select the **Support** function. It has one agent, **Sam**. Read the **Playbook** on the Function and then Sam\'s own **Instructions** — the playbook is what the whole team shares, the instructions are what makes Sam different from the next agent you add.',
      'Note Sam\'s **Access**: *Read and advise*. Sam may read your collections, but steps after Sam cannot change anything. That is the setting to use while you are deciding whether to trust an agent.',
      'Open **Sam: is this urgent?**. This is an **Ask an agent** step. Look at three things: the **task** (it asks for a decision, not just a look), the **decisions it can reach** (`urgent, routine`), and the **spend cap**.',
      'Open **Was it urgent?** — a Condition checking the context value `agent.verdict` against `urgent`. This is how a pipeline branches on a judgement rather than on stored data.',
      'Activate the pipeline with **▶ Run**. Then open the **Tickets** collection and add a record: Subject "Card payments failing at checkout since 9am", Detail "Every card is declined. Customers are leaving. Started this morning.", leave Priority blank. Save.',
      'The pipeline reaches Sam and **pauses** — an agent takes seconds to minutes, so the run waits rather than racing ahead. Open **Agents** in the sidebar: Sam shows as **Working**, with what this task has cost so far against its cap.',
      'When Sam finishes, the run resumes down whichever path the decision chose. Open **Agents** and click **Sam** — every run, with the task given, the answer, the cost and how long it took.',
      'Now open your **Inbox**. The message is not just Sam\'s answer: it is the whole run, step by step, with Sam\'s decision and the Condition saying what it compared — `agent.verdict was "urgent" — eq "urgent" → yes`.',
      'Add a second record that is plainly not urgent — "Could you change my invoice email address?" — and watch the same pipeline take the other branch.',
      'Try the thing that goes wrong: open Sam\'s step and change the task to just "Take a look at this ticket." Add another record. Sam will reason about it perfectly well and decide nothing, so the Condition falls to NO. Asking for the decision is the whole trick.',
    ],
    expect: 'A pipeline that hands a real decision to an agent, waits for it, and routes on the answer — with the cost visible and the run readable afterwards. Components used: Function with an agent, Collection, Trigger (record_added), Action (run_agent), Condition (on agent.verdict), Action (send_notification).',
    nodes: [
      {
        id: 'lab23-col', type: 'collectionNode', position: { x: 80, y: 200 },
        data: {
          label: 'Tickets', description: 'Incoming support tickets',
          fields: [
            { id: 'f1', name: 'Subject', type: 'text' },
            { id: 'f2', name: 'Detail', type: 'text' },
            { id: 'f3', name: 'Priority', type: 'select', options: ['Urgent', 'Routine'] },
          ],
          records: [],
        },
      },
      {
        id: 'lab23-trigger', type: 'triggerNode', position: { x: 340, y: 200 },
        data: {
          label: 'A ticket arrives', description: 'Fires when a ticket is added',
          triggerType: 'record_added', triggerCollectionId: 'lab23-col',
        },
      },
      {
        id: 'lab23-fn', type: 'functionNode', position: { x: 600, y: 400 },
        data: {
          label: 'Support', description: 'First line. Sorts what comes in.',
          playbook: 'You are the first-line support desk. You sort what arrives so the right people see it quickly. You never promise a customer a fix or a date — that is not yours to give.',
          agents: [
            {
              id: 'lab23-sam',
              name: 'Sam',
              role: 'Triage',
              model: 'fast',
              access: 'read_only',
              instructions: 'You decide how urgent a ticket is, and nothing else. You do not diagnose, and you do not suggest fixes.\n\nUrgent means people cannot do the thing they came to do, or money is being lost right now. A question, a request, or something with a workaround is routine — say so plainly even when the reporter sounds worried. Over-calling urgent is the failure that matters here: if everything is urgent, nothing is.',
            },
          ],
          tasks: [],
        },
      },
      {
        id: 'lab23-ask', type: 'actionNode', position: { x: 600, y: 200 },
        data: {
          label: 'Sam: is this urgent?',
          description: 'Hands the ticket to Sam. The run waits here.',
          actionType: 'run_agent',
          agentFunctionId: 'lab23-fn',
          agentId: 'lab23-sam',
          agentCapUsd: 0.25,
          agentVerdicts: 'urgent, routine',
          agentTask: 'A support ticket has just arrived.\n\nSubject: {record.subject}\nDetail: {record.detail}\n\nDecide whether this is urgent or routine, and say why in one or two sentences.',
        },
      },
      {
        id: 'lab23-cond', type: 'conditionNode', position: { x: 880, y: 200 },
        data: {
          label: 'Was it urgent?',
          description: 'Branches on what Sam decided',
          logic: 'and',
          conditions: [
            { source: 'context', contextKey: 'agent.verdict', operator: 'eq', value: 'urgent' },
          ],
        },
      },
      {
        id: 'lab23-urgent', type: 'actionNode', position: { x: 1140, y: 100 },
        data: {
          label: 'Wake someone up',
          description: 'What happens when Sam says urgent',
          actionType: 'send_notification',
          notificationSubject: 'URGENT: {record.subject}',
          notificationText: '{agent.name} called this urgent.\n\n{agent.result}',
          recipientType: 'owner',
          messageChannels: { inbox: true, email: false, sms: false },
        },
      },
      {
        id: 'lab23-routine', type: 'actionNode', position: { x: 1140, y: 300 },
        data: {
          label: 'Add it to the queue',
          description: 'What happens when Sam says routine',
          actionType: 'send_notification',
          notificationSubject: 'Routine: {record.subject}',
          notificationText: '{agent.name} called this routine.\n\n{agent.result}',
          recipientType: 'owner',
          messageChannels: { inbox: true, email: false, sms: false },
        },
      },
    ],
    edges: [
      { id: 'lab23-e1', source: 'lab23-col', target: 'lab23-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab23-e2', source: 'lab23-trigger', target: 'lab23-ask', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab23-e3', source: 'lab23-ask', target: 'lab23-cond', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab23-e4', source: 'lab23-cond', target: 'lab23-urgent', sourceHandle: 'yes', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab23-e5', source: 'lab23-cond', target: 'lab23-routine', sourceHandle: 'no', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 29,
    order: 13,
    title: 'Change a pipeline by asking',
    difficulty: 'Beginner',
    time: '6 min',
    summary: 'Describe a change to Buzz, read what it proposes, approve it, find it in the Logs, and undo it.',
    learns: [
      'Ask mode vs Change mode',
      'Reading the proposed change rather than the sentence above it',
      'Approving, and where the approval is recorded',
      'Undoing an applied change',
      'What gets refused, and why that is the feature working',
    ],
    steps: [
      'This uses **your** Anthropic key — open **Connectors → AI** and add one if you have not. A proposal costs a penny or two.',
      'Click **Deploy** to place the pipeline on your canvas: **Signups** → **A signup arrives** → **Welcome them**.',
      'Notice what is missing: nothing tells *you* when someone signs up. That is the change you are about to ask for.',
      'Open **Buzz AI** and switch the toggle above the input from **Ask** to **Change my pipeline**.',
      'Type: *Also send me an inbox message when a signup arrives, saying their name.* Press **Propose**.',
      'Read what comes back. The **italic line** is Buzz\'s understanding of your request. The **numbered list** is what will actually happen — and it is built from the change itself, not from Buzz\'s description of it. When they disagree, the list is the truth.',
      'Press **Approve**. Look at the canvas: the new step is there, wired in.',
      'Open **Logs**. There is an entry — *Pipeline changed via Buzz* — with what you asked for, what it did, and who approved it.',
      'Go back to the proposal in Buzz and press **Undo this change**. The step disappears; the pipeline is exactly as it was.',
      'Now ask for something impossible: *Add a database node that backs up to S3 every hour.* Buzz will tell you it cannot, rather than inventing something adjacent. That is the feature working.',
      'Finally, ask for something alarming: *Delete every node in this pipeline.* It will propose it — you asked — but read the top of the warnings: it states how much of your pipeline is going, in one line, before the list of individual removals.',
    ],
    expect: 'You can change a pipeline in plain English, see exactly what will happen before it does, find the approval in the Logs afterwards, and undo it. Components used: Buzz AI in Change mode, Collection, Trigger (record_added), Action (send_notification).',
    nodes: [
      {
        id: 'lab29-col', type: 'collectionNode', position: { x: 80, y: 200 },
        data: {
          label: 'Signups', description: 'People who signed up',
          fields: [
            { id: 'f1', name: 'Name', type: 'text' },
            { id: 'f2', name: 'Email', type: 'text' },
          ],
          records: [],
        },
      },
      {
        id: 'lab29-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'A signup arrives', description: 'Fires when a signup is added',
          triggerType: 'record_added', triggerCollectionId: 'lab29-col',
        },
      },
      {
        id: 'lab29-welcome', type: 'actionNode', position: { x: 680, y: 200 },
        data: {
          label: 'Welcome them', description: 'Greets the person who signed up',
          actionType: 'send_notification',
          notificationSubject: 'Welcome, {record.name}',
          notificationText: 'Thanks for signing up.',
          recipientType: 'owner',
          messageChannels: { inbox: true, email: false, sms: false },
        },
      },
    ],
    edges: [
      { id: 'lab29-e1', source: 'lab29-col', target: 'lab29-trigger', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab29-e2', source: 'lab29-trigger', target: 'lab29-welcome', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
  {
    id: 28,
    order: 28,
    title: 'Connect another app',
    difficulty: 'Intermediate',
    time: '8 min',
    summary: 'Mirror a connected app\'s data into a collection, trigger on a change made over there, and write back to it — without writing a line of JSON.',
    learns: [
      'Connector-backed collections (live data, not a copy)',
      'Why a mirrored row is read-only here',
      'Triggering on a change made in the other app',
      'Guided connector actions instead of raw HTTP',
    ],
    steps: [
      'This lab needs a **taskDojo** connector. Open **Connectors**, choose taskDojo, and paste a token from taskDojo\'s own Settings → Connectors. Projectly checks it before saving, so if it saves, it works.',
      'Click **Deploy** below to place the pipeline on your canvas.',
      'Click the **taskDojo Tasks** collection to open its panel, then set **Source** to **🔌 taskDojo — live tasks**. The field list fills itself in from the connector. Click **Sync now** — your real tasks appear.',
      'Try editing a row. It refuses: these rows belong to taskDojo, and the next sync would overwrite anything you changed here. The pipeline changes them by calling taskDojo\'s API instead — the only write that sticks.',
      'Open **Tag changed to blocked** and confirm the collection, field (**Tag**) and value (**blocked**) are set.',
      'Open **Comment on the task** and **Set it to in progress**. Notice you pick *what the step does* from a dropdown, and Status offers only the six values taskDojo accepts — no endpoint or JSON to write.',
      'Activate the pipeline, then go to **taskDojo** and change an existing task\'s tag to **blocked**. Within a minute you get an inbox message here, and the task over there gains a comment and moves to In Progress.',
    ],
    expect: 'A change made in another app runs a Projectly pipeline, which writes back to that app. Components used: Collection (connector-backed), Trigger (field_change), Action (send_notification), Action (http_request via connector operations).',
    nodes: [
      {
        id: 'lab28-col', type: 'collectionNode', position: { x: 80, y: 200 },
        data: {
          label: 'taskDojo Tasks',
          description: 'Set Source to taskDojo — the fields come from the connector',
          // Deliberately left unsourced: choosing the source IS the lesson, and
          // hardcoding the connector's field list here would drift from the
          // adapter that actually does the syncing.
          fields: [
            { id: 'title', name: 'Title', type: 'text' },
            { id: 'tag', name: 'Tag', type: 'text' },
          ],
          records: [],
        },
      },
      {
        id: 'lab28-trigger', type: 'triggerNode', position: { x: 380, y: 200 },
        data: {
          label: 'Tag changed to blocked',
          description: 'Fires when someone tags a task "blocked" in the other app',
          triggerType: 'field_change', triggerCollectionId: 'lab28-col',
          triggerFieldId: 'tag', triggerValue: 'blocked',
        },
      },
      {
        id: 'lab28-notify', type: 'actionNode', position: { x: 680, y: 120 },
        data: {
          label: 'Tell me about it',
          description: 'Inbox message naming the task',
          actionType: 'send_notification',
          notificationSubject: 'Task blocked: {record.title}',
          notificationText: '{record.title} was tagged "{record.tag}" in taskDojo.',
          recipientType: 'owner',
          messageChannels: { inbox: true, email: false, sms: false },
        },
      },
      {
        id: 'lab28-comment', type: 'actionNode', position: { x: 680, y: 280 },
        data: {
          label: 'Comment on the task',
          description: 'Writes a comment back into taskDojo',
          actionType: 'http_request',
          httpOperationId: 'comment_task',
          httpOperationParams: {
            taskId: '{record.id}',
            text: 'Picked up by Projectly — tagged {record.tag}.',
            author: 'Projectly',
          },
          httpMethod: 'POST',
          httpEndpoint: '/tasks/{record.id}/comments',
          httpBody: '{\n  "text": "Picked up by Projectly — tagged {record.tag}.",\n  "author": "Projectly"\n}',
        },
      },
      {
        id: 'lab28-status', type: 'actionNode', position: { x: 980, y: 280 },
        data: {
          label: 'Set it to in progress',
          description: 'Changes the real task\'s status',
          actionType: 'http_request',
          httpOperationId: 'update_task',
          httpOperationParams: { taskId: '{record.id}', status: 'in_progress' },
          httpMethod: 'PATCH',
          httpEndpoint: '/tasks/{record.id}',
          httpBody: '{\n  "status": "in_progress"\n}',
        },
      },
    ],
    edges: [
      { id: 'lab28-e1', source: 'lab28-trigger', target: 'lab28-notify', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab28-e2', source: 'lab28-notify', target: 'lab28-comment', animated: true, style: { stroke: '#94a3b8' } },
      { id: 'lab28-e3', source: 'lab28-comment', target: 'lab28-status', animated: true, style: { stroke: '#94a3b8' } },
    ],
  },
];

const NODE_ICONS = {
  collectionNode: '▦', triggerNode: '⚡', actionNode: '▶', conditionNode: '◇',
  functionNode: '⬡', gateNode: '◈', connectionNode: '⬢', noteNode: '▭',
};

function formatStep(text) {
  // Escape HTML first so code samples (curl, JSON) render literally and safely.
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="labs-step-code">$1</code>')
    .replace(/\n/g, '<br>');
}

export default function LabsView({ workspaceId, workspaceName, onNavigate, onStartCompanion, initialLabId }) {
  const [deploying, setDeploying] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeLab, setActiveLab] = useState(LABS[0].id);
  // How many nodes are already on this workspace's canvas. We track it up front
  // (not just at click time) so the Deploy button can show the "canvas isn't
  // empty" state before the user clicks — the old code only discovered it after
  // an async fetch inside the click handler, so the first click looked dead.
  const [canvasNodes, setCanvasNodes] = useState(null); // null = unknown/loading

  const refreshCanvas = useCallback(async () => {
    if (!workspaceId) { setCanvasNodes(0); return; }
    try {
      const pipeline = await dataService.getPipeline(workspaceId);
      setCanvasNodes(pipeline?.nodes?.length || 0);
    } catch {
      setCanvasNodes(0);
    }
  }, [workspaceId]);

  useEffect(() => { refreshCanvas(); }, [refreshCanvas]);

  // Deep-link: when arriving from a University "Try it in Labs" link, open that
  // lab directly.
  useEffect(() => {
    if (initialLabId && LABS.some(l => l.id === initialLabId)) setActiveLab(initialLabId);
  }, [initialLabId]);

  // Labs are sorted by an explicit `order` (easy → hard) so the visible number
  // reflects position, not the internal id — this lets us reorder/insert labs
  // without renumbering node ids or touching deployed pipeline data.
  const orderedLabs = [...LABS].sort((a, b) => a.order - b.order);
  const lab = orderedLabs.find(l => l.id === activeLab) || orderedLabs[0];
  const labPos = orderedLabs.findIndex(l => l.id === lab.id) + 1;

  const handleDeploy = async (lab) => {
    setError(null);
    setSuccess(null);

    if (!workspaceId) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    // Re-check the canvas right before deploying (state may be stale if the user
    // edited the pipeline in another tab). If it isn't empty, offer to clear it
    // and deploy rather than silently refusing — this is what lets you run labs
    // back-to-back in one workspace instead of needing a fresh one each time.
    const pipeline = await dataService.getPipeline(workspaceId);
    const existing = pipeline?.nodes?.length || 0;
    setCanvasNodes(existing);

    if (existing > 0) {
      const ok = await confirmDialog({
        title: 'Clear canvas & deploy?',
        message: `Your canvas already has ${existing} node${existing === 1 ? '' : 's'}. ` +
          `Deploying "${lab.title}" will clear the current pipeline and replace it.`,
        confirmText: 'Clear & deploy', danger: true,
      });
      if (!ok) return;
    } else {
      const confirmed = await confirmDialog({
        title: 'Deploy lab',
        message: `Deploy "${lab.title}" in project "${workspaceName || 'current workspace'}"?`,
        confirmText: 'Deploy',
      });
      if (!confirmed) return;
    }

    setDeploying(lab.id);
    try {
      // Clear any existing pipeline first so the new lab is the only thing on the
      // canvas (deployPipeline replaces nodes/edges, but this also drops the
      // active flag cleanly before we re-seed and re-activate).
      if (existing > 0) {
        await dataService.savePipeline(workspaceId, [], [], false);
      }
      await dataService.deployPipeline(workspaceId, lab.nodes, lab.edges, true);
      window.dispatchEvent(new CustomEvent('projectly_collection_changed', { detail: { nodes: lab.nodes } }));
      setSuccess(lab.id);
      setTimeout(() => onNavigate('pipeline'), 1200);
    } catch (e) {
      console.error('[LabsView:deploy]', e);
      setError(`Failed to deploy: ${e.message}`);
    } finally {
      setDeploying(null);
    }
  };

  return (
    <div className="labs-layout">
      {/* Sidebar */}
      <div className="labs-sidebar">
        <div className="labs-sidebar-top">
          <div className="labs-logo">Labs</div>
          <div className="labs-logo-sub">Hands-on exercises</div>
        </div>
        <nav className="labs-nav">
          {orderedLabs.map((l, i) => (
            <button
              key={l.id}
              className={`labs-nav-item${activeLab === l.id ? ' active' : ''}`}
              onClick={() => { setActiveLab(l.id); setError(null); setSuccess(null); }}
            >
              <span className="labs-nav-num">{i + 1}</span>
              <span className="labs-nav-label">{l.title}{l.comingSoon ? ' 🔒' : ''}</span>
              <span className={`labs-nav-diff labs-nav-diff--${l.difficulty.toLowerCase()}`}>{l.difficulty}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="labs-content">
        <div className="labs-breadcrumb">Lab {labPos} of {orderedLabs.length}</div>
        <h1 className="labs-title">{lab.title}</h1>
        <div className="labs-meta-row">
          <span className={`labs-diff-badge labs-diff-badge--${lab.difficulty.toLowerCase()}`}>{lab.difficulty}</span>
          <span className="labs-time-badge">{lab.time}</span>
          <span className="labs-node-count">{lab.nodes.length} node{lab.nodes.length !== 1 ? 's' : ''}</span>
        </div>

        {lab.comingSoon && (
          <div className="labs-tip" style={{ borderLeftColor: '#f59e0b', background: '#fffbeb' }}>
            🔒 <strong>Coming soon.</strong> {lab.comingSoon}
          </div>
        )}

        <p className="labs-summary">{lab.summary}</p>

        {onNavigate && LAB_TO_ARTICLE[lab.id] && (
          <button
            className="labs-crosslink"
            onClick={() => onNavigate('university', LAB_TO_ARTICLE[lab.id])}
            title="Read the concept behind this lab in University"
          >
            📖 Read the concept in University →
          </button>
        )}

        {error && (
          <div className="labs-error">
            <span>{error}</span>
            <button className="labs-error-close" onClick={() => setError(null)}>x</button>
          </div>
        )}
        {success && (
          <div className="labs-success">Deployed! Redirecting to Pipeline...</div>
        )}

        {/* What you'll learn */}
        <div className="labs-section">
          <h3 className="labs-section-heading">What you'll learn</h3>
          <div className="labs-learn-chips">
            {lab.learns.map((item, i) => (
              <span key={i} className="labs-learn-chip">{item}</span>
            ))}
          </div>
        </div>

        {/* Nodes that will be deployed */}
        <div className="labs-section">
          <h3 className="labs-section-heading">Pipeline overview</h3>
          <div className="labs-node-list">
            {lab.nodes.map(n => (
              <div key={n.id} className="labs-node-row" data-type={n.type}>
                <span className="labs-node-icon">{NODE_ICONS[n.type] || '?'}</span>
                <div className="labs-node-info">
                  <span className="labs-node-name">{n.data.label || n.data.title}</span>
                  <span className="labs-node-type">{n.type.replace('Node', '')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div className="labs-section">
          <h3 className="labs-section-heading">Steps</h3>
          <div className="labs-tip">
            💡 <strong>How records fire a pipeline:</strong> when you add a record, fill the draft row and click
            the green <strong>✓</strong> tick on the row. The trigger fires on <strong>save</strong> — not while you type — so a
            half-filled row never runs the pipeline by accident.
          </div>
          <ol className="labs-steps">
            {lab.steps.map((step, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatStep(step) }} />
            ))}
          </ol>
        </div>

        {/* Expected result */}
        <div className="labs-section">
          <h3 className="labs-section-heading">Expected result</h3>
          <div className="labs-expect">{lab.expect}</div>
        </div>

        {/* Deploy */}
        {canvasNodes > 0 && (
          <div className="labs-tip" style={{ borderLeftColor: '#f59e0b' }}>
            ⚠️ <strong>This workspace's canvas already has {canvasNodes} node{canvasNodes === 1 ? '' : 's'}.</strong> Labs
            deploy to an empty canvas — deploying will <strong>clear the current pipeline and replace it</strong> with this
            lab. To keep your existing work, switch to a fresh workspace first.
          </div>
        )}
        <div className="labs-deploy-row">
          <button
            data-testid="lab-deploy"
            className="labs-deploy-btn"
            onClick={() => handleDeploy(lab)}
            disabled={deploying === lab.id}
          >
            {deploying === lab.id
              ? 'Deploying...'
              : canvasNodes > 0
                ? `Clear canvas & deploy Lab ${labPos}`
                : `Deploy Lab ${labPos} to Canvas`}
          </button>
          {onStartCompanion && (
            <button
              className="labs-companion-btn"
              onClick={() => onStartCompanion(lab)}
              title="Keep these steps visible while you work in the Pipeline"
            >
              📌 Follow steps in Pipeline
            </button>
          )}
        </div>

        <div className="labs-footer">
          Labs deploy pre-built nodes to an empty canvas so you can explore each concept hands-on. Running another lab
          replaces the current one — to keep your work, use a fresh workspace.
          Use <strong>Follow steps in Pipeline</strong> to keep a floating checklist on screen while you build.
        </div>
      </div>
    </div>
  );
}
